// types/entities.ts - Доменні моделі та сутності

import { TermsOfLeaseType } from './leases';
import { IUnit } from '@/types/units';

export enum PaymentPeriod {
  WEEK = 1,
  MONTH = 2,
}

export enum PaymentStatus {
  NO_DATA = 0,
  DEBT = 1,
  PAID = 2,
  PENDING = 3,
}

export enum PaymentMethod {
  OTHER = 1,
  CASH = 2,
  CHEQUE = 3,
  ELECTRONIC_TRANSFER = 4,
}

export enum UserRole {
  OWNER = 'owner',
  MANAGER = 'manager',
  TENANT = 'tenant',
}

// === БАЗОВІ ІНТЕРФЕЙСИ ===

export interface ILease {
  isFixed: boolean | TermsOfLeaseType | null;
  period?: number | string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  indeterminateStartDate?: Date | null;
}

export interface IPersonal {
  personal_country?: string;
  personal_province?: string;
  personal_city?: string;
  personal_address?: string;
  personal_apt_suite?: string;
  personal_unit?: string;
  personal_municipality?: string;
  personal_postal_code?: string;
  personal_phone?: string;
  personal_info_agreement?: string;
  terms_privacy_agreement?: string;
  personal_age_group?: string | null;
}

export interface IUser {
  id: number;
  avatar?: string | null;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  addresses: Partial<IAddress> | null;
  role: UserRole;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date;
  deleted: boolean;
  password?: string | null;
  confirmationToken?: string | null;
  isEmailConfirmed?: boolean | null;
  unreadNotificationsCount?: number;
  personal?: IPersonal;
}

export type IAddress = {
  id?: number;
  country: string;
  stateOrProvince?: string | null;
  postalCode?: string | null;
  city?: string | null;
  street?: string | null;
  buildingNumber?: string | null;
  apartmentNumber?: string | null;
  municipality?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  phone?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  unitsCount?: number;
  leasedCount?: number;
  vacantCount?: number;
  zip?: string | null;
};

export interface IProperty {
  id?: number;
  name: string;
  address: IAddress;
  owner?: IUser | null;
  units?: IUnit[];
  unitsCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
  type?: 'residential' | 'commercial' | 'mixed';
  leased?: number;
  vacant?: number;
  constructionYear?: string | null;
  foundation?: string | null;
  floors?: string | null;
  dimension?: string | null;
  hasBasement?: boolean | null;
  basementDimension?: string | null;
  basementDescription?: string | null;
  hasExterior?: boolean | null;
  exteriorDimension?: string | null;
  exteriorZoning?: string | null;
  hasRestrictions?: boolean | null;
  restrictionsDescription?: string | null;
}

export interface IPayment {
  id: number;
  unit?: IUnit;
  status: PaymentStatus;
  amount: number;
  date: Date;
  description: string | null;
  method: PaymentMethod;
  createdAt: Date;
  updatedAt: Date;
}

export interface Invite {
  id: number;
  token: string;
  unit?: IUnit | null;
  email?: string | null;
  isUsed: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface IUnitCharacteristics {
  parking: {
    hasIndoor: boolean;
    hasOutdoor: boolean;
    indoorCount: number;
    outdoorCount: number;
    indoorSpaces: string;
    outdoorSpaces: string;
  };
  storage: {
    hasLockerOrStorage: boolean;
    storageDescription: string;
  };
  otherAccessories: {
    hasOtherAccessories: boolean;
    otherAccessoriesDescription: string;
  };
  furniture: {
    beds: {
      count: number;
      included: boolean;
    };
    chairs: {
      count: number;
      included: boolean;
    };
    tables: {
      count: number;
      included: boolean;
    };
    couches: {
      count: number;
      included: boolean;
    };
    armchairs: {
      count: number;
      included: boolean;
    };
    chests: {
      count: number;
      included: boolean;
    };
    appliances: {
      dryer: boolean;
      stove: boolean;
      washer: boolean;
      microwave: boolean;
      dishwasher: boolean;
      refrigerator: boolean;
    };
    isIncluded: boolean | null;
    hasOther: boolean;
    otherDescription: string;
  };
}



// === ДОМЕННІ МОДЕЛІ З API (перенесені) ===

export interface Payment {
  id: string;
  status: string;
}

export interface TenantLease {
  id: number;
  unitId: number;
  tenantId: number;
  startDate: string | null;
  endDate: string | null;
  status: string;
  monthlyRent: string;
  unit: IUnit & { property: IProperty };
  payments?: Payment[];
}

// Нова структура lease (замінює LeaseInfo та PendingLeaseInfo)
export interface UnitLease {
  id: number;
  status: 'active' | 'pending';
  startDate?: string | null;
  endDate?: string | null;
  monthlyRent: string;
  tenantId?: number | null;
  inviteEmail?: string | null; // Для pending lease
}

// Інформація про tenant
export interface UnitTenant {
  id: number;
  name: string;
  email: string;
  avatar?: string | null;
}

// Оновлена структура юніта (під нову backend архітектуру)
export interface UnitWithLeaseInfo {
  id: number;
  number: string;
  type?: string;
  floor?: string;
  rooms?: number;
  dimension?: number;
  bedrooms?: number;
  bathrooms?: number;
  tenant?: UnitTenant | null;
  paymentStatus: 'paid' | 'debt' | 'not-set';
  createdAt: string;
  updatedAt: string;
  lease?: UnitLease | null;
  characteristics?: Partial<IUnitCharacteristics>;
  rentPricePeriod?: string;
  costOfServices?: number;
  totalRentPrice?: number;
}

// Для lease історії
export interface LeaseHistoryItem {
  id: number;
  status: 'active' | 'ended' | 'terminated' | 'pending';
  startDate?: string;
  endDate?: string;
  monthlyRent: string;
  tenantId: number;
  tenant?: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  } | null;
}

export interface DetailedUnitResponse {
  id: number;
  number: string;
  type?: string;
  floor?: number | null;
  roomsCount?: number | null;  // API повертає roomsCount, не rooms
  rooms?: Array<{              // API повертає rooms як масив, не число
    type?: string | null;
    floor?: number | null;
    flooring?: string | null;
    dimension?: number | null;
  }>;
  dimension?: number | null;
  bedrooms?: number | null;
  numberOfFloors?: number | null;
  bathrooms?: number | null;
  hasOtherInformation?: boolean;
  otherInformation?: string | null;
  characteristics?: Partial<IUnitCharacteristics>;
  createdAt: string;
  updatedAt: string;
  tenant?: UnitTenant | null;
  lease?: UnitLease | null;
  leaseHistory: LeaseHistoryItem[];
  property: {
    id: number;
    name: string;
    type: string;
    unitsCount?: number;
    address?: {
      id: number;
      street: string;
      city: string;
      country: string;
      postalCode: string;
      latitude?: number;
      longitude?: number;
      formatted?: string;
    } | null;
  };
}

// Тип для відповіді від /units/vacant
export interface VacantUnitFromApi {
  id: number;
  number: string;
  rentPrice: number | null;
  vacateDate: string | null;
  vacantDays: number;
  property: {
    id: number;
    name: string;
    type: string;
    address: {
      id: number;
      country: string;
      stateOrProvince: string;
      city: string;
      street: string;
      buildingNumber: string;
      postalCode: string;
      latitude: number;
      longitude: number;
    };
  };
}

// === LEASE МОДЕЛІ (з lease.ts) ===

// Власний тип для tenant контексту
export interface TenantLeaseInfo {
  id: number;
  status: 'active' | 'ended' | 'terminated' | 'pending';
  startDate: string;
  endDate?: string;
  monthlyRent: number;
  tenant: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  allowEarlyTermination?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantLeaseListItem {
  id: number;
  propertyName: string;
  unitNumber: string;
  unitId: number;
  propertyId: number;
  monthlyRent: number;
  startDate: string;
  endDate?: string;
  status: 'active' | 'terminated' | 'expired';
  propertyAddress: {
    street: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  landlord: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  allowEarlyTermination?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantLeaseDetails {
  id: number;
  status: 'active' | 'terminated' | 'expired';
  startDate: string;
  endDate?: string;
  monthlyRent: number;
  depositAmount?: number;
  allowEarlyTermination: boolean;

  // Інформація про property та unit
  property: {
    id: number;
    name: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
    };
    description?: string;
  };

  unit: {
    id: number;
    number: string;
    description?: string;
  };

  // Інформація про landlord
  landlord: {
    id: number;
    name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };

  // Умови lease
  terms?: {
    petPolicy?: string;
    smokingPolicy?: string;
    guestPolicy?: string;
    maintenanceResponsibilities?: string;
    utilities?: string[];
  };

  // Документи (якщо потрібно)
  documents?: {
    id: number;
    name: string;
    type: 'contract' | 'addendum' | 'receipt' | 'other';
    url?: string;
    uploadedAt: string;
  }[];

  // Історія платежів (основна інформація)
  paymentHistory?: {
    totalPaid: number;
    lastPaymentDate?: string;
    nextPaymentDue?: string;
    isCurrentlyOwed: boolean;
    overdueAmount?: number;
  };

  createdAt: string;
  updatedAt: string;
}

export interface TerminateLeaseResponse {
  lease: TenantLeaseDetails;
  terminationDate: string;
  message: string;
}

// === INVITE МОДЕЛІ (з invite.ts) ===

export interface InviteInfo {
  id: string;
  token: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';

  // Інформація про property та unit
  property: {
    id: number;
    name: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
    };
    description?: string;
  };

  unit: {
    id: number;
    number: string;
    description?: string;
  };

  // Умови lease
  monthlyRent: number;
  depositAmount?: number;
  leaseTermMonths?: number;
  allowPets?: boolean;
  allowSmoking?: boolean;

  // Інформація про landlord
  landlord: {
    id: number;
    name: string;
    email: string;
    phone?: string;
    avatar?: string;
    companyName?: string;
  };

  // Терміни запрошення
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
}

export interface AcceptInviteResponse {
  invite: Invite;
  lease: {
    id: number;
    status: 'active';
    startDate: string;
    endDate?: string;
    monthlyRent: number;
    unit: {
      id: number;
      number: string;
      property: {
        id: number;
        name: string;
        address: string;
      };
    };
    tenant: {
      id: number;
      name: string;
      email: string;
      avatar?: string;
    };
    landlord: {
      id: number;
      name: string;
      email: string;
    };
    createdAt: string;
  };
}

// === CHAT МОДЕЛІ ===

export interface Message {
  id: number;
  text: string;
  chat?: Chat;
  sender: IUser;
  createdAt: string;
  isRead: boolean;
}

export interface Chat {
  id: number;
  title?: string | null;
  messages: Message[];
  createdAt: string;
  lastMessageAt: string;
  participants: IUser[];
  property?: IProperty | null;
}

export interface ChatsResponse extends Chat {
  message?: string | null;
  unreadMessagesCount?: number;
}

export interface SendMessageRequest {
  text: string;
}

export interface OnSocketNewMessage {
  id: number;
  chatId: number;
  text: string;
  createdAt: string;
  participants: { id: number; firstname: string; lastname: string }[];
  sender: IUser;
}
